import { Driver } from '@citeproc-rs/wasm';
import { styles } from '../config/styles';
import { withBasePath } from '_config/basePath';

interface DriverConfig {
  lang: string;
  style: string;
}

interface CitationFetcher {
  fetchLocale: (lang: string) => Promise<string>;
}

class DriverError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DriverError';
  }
}

export const createCitationFetcher = (): CitationFetcher => ({
  fetchLocale: async (lang: string): Promise<string> => {
    try {
      const response = await fetch(withBasePath(`/citations/locales-${lang}.xml`));
      if (!response.ok) {
        throw new DriverError(`Failed to fetch locale: ${response.statusText}`);
      }
      return await response.text();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new DriverError(`Error fetching locale: ${errorMessage}`);
    }
  }
});

export const getStyleContent = async (style: string): Promise<string> => {
  try {
    const response = await fetch(withBasePath(`/citations/${style}.csl`));
    if (!response.ok) {
      throw new DriverError(`Failed to fetch style: ${response.statusText}`);
    }
    return await response.text();
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new DriverError(`Error fetching style: ${errorMessage}`);
  }
};

export const createDriver = async ({ lang, style }: DriverConfig): Promise<Driver> => {
  try {
    const fetcher = createCitationFetcher();
    const styleContent = await getStyleContent(style);
    
    const driver = new Driver({
      style: styleContent,
      fetcher,
      localeOverride: lang,
    });

    driver.includeUncited("All");
    await driver.fetchLocales();
    
    return driver;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new DriverError(`Failed to create citation driver: ${errorMessage}`);
  }
};

export const getStyleForDocument = (refStyle: string, refDisplay: string): string => {
  const styleConfig = styles[refStyle];
  if (!styleConfig) {
    throw new DriverError(`Unknown reference style: ${refStyle}`);
  }

  const style = styleConfig[refDisplay];
  if (!style) {
    throw new DriverError(`Unknown display style ${refDisplay} for reference style ${refStyle}`);
  }

  return style;
};